// src/utils/graphqlFetch.ts
// Resilient GraphQL fetch wrapper with automatic token refresh.
//
// Replaces raw fetch(GRAPHQL_API_URL, ...) calls across the app so that:
//  1. Token expiry is checked before every request; if close to expiring, refresh first.
//  2. Authorization header is added automatically.
//  3. A 401 is handled by the global fetch interceptor (fetchInterceptor.ts),
//     which refreshes and retries, and signs the tab out only if the server
//     refuses the session. Callers get `{ data: null, errors }` for a 401 the
//     interceptor couldn't recover.

import { getAuthToken } from './cookieUtils';
import {
  refreshAccessToken,
  shouldRefreshToken,
  isRedirectingToLogin
} from '../services/authService';
import { GRAPHQL_API_URL } from '../constants';

async function ensureFreshToken(): Promise<void> {
  if (!shouldRefreshToken()) return;
  // refreshAccessToken dedups concurrent callers in this tab and across tabs.
  await refreshAccessToken();
}

/**
 * Execute a GraphQL query/mutation with automatic auth handling.
 *
 * Returns the parsed JSON body (`{ data, errors }`).
 */
export async function graphqlFetch(
  query: string,
  variables?: Record<string, any>
): Promise<any> {
  // --- Pre-request: proactively refresh if token is about to expire ---
  try {
    await ensureFreshToken();
  } catch {
    // If proactive refresh fails we still attempt the request — the token
    // may still be valid for a while.
  }

  const token = getAuthToken();
  const response = await fetch(GRAPHQL_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ query, variables })
  });

  if (response.status === 401) {
    // Return a minimal object so callers don't crash
    const message = isRedirectingToLogin()
      ? 'Session expired'
      : 'Could not reach FreightRoll. Please try again.';
    return { data: null, errors: [{ message }] };
  }

  return response.json();
}

export { GRAPHQL_API_URL };
